import { useState, useMemo } from 'react';
import { Search, Users, Globe } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { useCrewPortal } from '../../hooks/useCrewPortal';
import { EmptyState } from '../common/EmptyState';
import type { TeamMember } from '../../types';

interface TeamRosterProps {
  members: TeamMember[];
  onEditMember?: (member: TeamMember) => void;
  onRefresh?: () => void;
  onToast?: (msg: string, type: 'success' | 'error') => void;
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function getUserColor(member: TeamMember): string {
  if (member.employmentType === 'contractor') return palette.amber;
  if (member.employmentType === 'operator') return palette.blue;
  if (member.role === 'owner') return palette.accent;
  if (member.role === 'manager') return palette.accentDeep;
  return palette.green;
}

function statusStyle(status: string): { bg: string; color: string } {
  if (status === 'active') return { bg: palette.greenWash, color: palette.green };
  if (status === 'invited') return { bg: palette.amberWash, color: palette.amber };
  return { bg: palette.g100, color: palette.g400 };
}

type FilterType = 'all' | 'employee' | 'contractor' | 'operator';
type SortKey = 'name' | 'role' | 'hours' | 'status';

const ROLE_ORDER: Record<string, number> = { owner: 0, manager: 1, employee: 2 };

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`, cursor: 'pointer', whiteSpace: 'nowrap',
  userSelect: 'none',
};

const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

export function TeamRoster({ members, onEditMember, onRefresh, onToast }: TeamRosterProps) {
  const { createPortal, reactivatePortal } = useCrewPortal();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortKey, setSortKey] = useState<SortKey>('role');
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = members;
    if (filter !== 'all') list = list.filter(m => m.employmentType === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(q) ||
        (m.email ?? '').toLowerCase().includes(q) ||
        (m.phone ?? '').includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      else if (sortKey === 'role') cmp = (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9);
      else if (sortKey === 'hours') cmp = a.hoursThisPeriod - b.hoursThisPeriod;
      else if (sortKey === 'status') cmp = a.status.localeCompare(b.status);
      return sortAsc ? cmp : -cmp;
    });
  }, [members, search, filter, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  }

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'employee', label: 'Employees' },
    { key: 'contractor', label: 'Contractors' },
    { key: 'operator', label: 'Operators' },
  ];

  return (
    <div>
      {/* Search + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`,
          background: palette.white, flex: '0 1 260px',
        }}>
          <Search size={14} color={palette.g400} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search team..."
            style={{ border: 'none', outline: 'none', fontSize: 13, color: palette.g800, flex: 1, fontFamily: 'inherit', background: 'transparent' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 6, padding: 2 }}>
          {filters.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
              background: filter === f.key ? palette.g800 : 'transparent',
              color: filter === f.key ? palette.white : palette.g500,
              transition: 'all 0.12s',
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH} onClick={() => toggleSort('name')}>Name {sortKey === 'name' ? (sortAsc ? '↑' : '↓') : ''}</th>
              <th style={TH} onClick={() => toggleSort('role')}>Role {sortKey === 'role' ? (sortAsc ? '↑' : '↓') : ''}</th>
              <th style={TH}>Type</th>
              <th style={TH} onClick={() => toggleSort('status')}>Status {sortKey === 'status' ? (sortAsc ? '↑' : '↓') : ''}</th>
              <th style={TH}>Rate</th>
              <th style={TH} onClick={() => toggleSort('hours')}>Hours (week) {sortKey === 'hours' ? (sortAsc ? '↑' : '↓') : ''}</th>
              <th style={TH}>Phone</th>
              <th style={TH}>App</th>
              <th style={TH}>Portal</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 0 }}><EmptyState icon={<Users size={22} />} title="No team members found" subtitle="Adjust your filters or add someone to the team." /></td></tr>
            ) : filtered.map(m => {
              const expanded = expandedId === m.id;
              const ss = statusStyle(m.status);
              return (
                <>{/* Fragment with key on first tr */}
                  <tr
                    key={m.id}
                    onClick={() => onEditMember ? onEditMember(m) : setExpandedId(expanded ? null : m.id)}
                    style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={TD}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 14, background: getUserColor(m),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 600, color: palette.white, flexShrink: 0,
                        }}>{getInitials(m.firstName, m.lastName)}</div>
                        <span style={{ fontWeight: 500, color: palette.g800 }}>{m.firstName} {m.lastName}</span>
                      </div>
                    </td>
                    <td style={{ ...TD, textTransform: 'capitalize' }}>{m.role}</td>
                    <td style={TD}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4,
                        background: m.employmentType === 'contractor' ? palette.amberWash : m.employmentType === 'operator' ? palette.blueWash : palette.greenWash,
                        color: m.employmentType === 'contractor' ? palette.amber : m.employmentType === 'operator' ? palette.blue : palette.green,
                        textTransform: 'capitalize',
                      }}>{m.employmentType}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, background: ss.bg, color: ss.color, textTransform: 'capitalize' }}>
                        {m.status}
                      </span>
                    </td>
                    <td style={TD}>{m.employmentType === 'contractor' && m.contractorProfile?.flatRate ? `$${m.contractorProfile.flatRate}/flat` : m.hourlyRate ? `$${m.hourlyRate}/hr` : '—'}</td>
                    <td style={{ ...TD, color: m.overtimeHours > 0 ? palette.red : palette.g700, fontWeight: m.overtimeHours > 0 ? 600 : 400 }}>
                      {m.hoursThisPeriod > 0 ? `${m.hoursThisPeriod.toFixed(1)}h` : '—'}
                      {m.overtimeHours > 0 && <span style={{ fontSize: 10, marginLeft: 4 }}>({m.overtimeHours.toFixed(1)} OT)</span>}
                    </td>
                    <td style={{ ...TD, fontSize: 12, color: palette.g500 }}>{m.phone ?? '—'}</td>
                    <td style={TD}>
                      <div style={{ width: 8, height: 8, borderRadius: 4, background: m.appActivated ? palette.green : palette.g300 }} />
                    </td>
                    <td style={TD}>
                      {m.portalSlug && m.portalActive ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`https://app.crewbit.ca/crew/${m.portalSlug}`); onToast?.('Link copied', 'success'); }}
                          title={`app.crewbit.ca/crew/${m.portalSlug}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                        >
                          <Globe size={14} color={palette.green} />
                          <span style={{ fontSize: 11, fontWeight: 500, color: palette.green }}>Active</span>
                        </button>
                      ) : m.portalSlug && !m.portalActive ? (
                        <button
                          onClick={async (e) => { e.stopPropagation(); const r = await reactivatePortal(m.id); if (r) { onRefresh?.(); onToast?.('Portal reactivated', 'success'); } }}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                        >
                          <Globe size={14} color={palette.g400} />
                          <span style={{ fontSize: 11, fontWeight: 500, color: palette.g400 }}>Inactive</span>
                        </button>
                      ) : !m.appActivated ? (
                        <button
                          onClick={async (e) => { e.stopPropagation(); const r = await createPortal(m.id); if (r) { navigator.clipboard.writeText(r.url); onRefresh?.(); onToast?.('Portal created — link copied', 'success'); } }}
                          style={{ fontSize: 11, fontWeight: 500, color: palette.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                        >
                          + Create
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: palette.g300 }}>—</span>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr key={`${m.id}-exp`}>
                      <td colSpan={8} style={{ padding: '12px 20px 16px', background: palette.g50, borderBottom: `1px solid ${palette.g100}` }}>
                        <div style={{ display: 'flex', gap: 32, fontSize: 12.5, color: palette.g600, flexWrap: 'wrap' }}>
                          <div><span style={{ color: palette.g400 }}>Email:</span> {m.email ?? '—'}</div>
                          <div><span style={{ color: palette.g400 }}>Activated:</span> {m.activatedAt ? new Date(m.activatedAt).toLocaleDateString('en-CA') : 'Not yet'}</div>
                          <div><span style={{ color: palette.g400 }}>Certs:</span> {m.certCount} active{m.certExpiringSoon > 0 ? `, ${m.certExpiringSoon} expiring` : ''}</div>
                          <div><span style={{ color: palette.g400 }}>Shifts this week:</span> {m.shiftsThisPeriod}</div>
                          {m.contractorProfile && (
                            <div><span style={{ color: palette.g400 }}>Company:</span> {m.contractorProfile.companyName ?? '—'} · {m.contractorProfile.tradeOrSpecialty ?? '—'}</div>
                          )}
                          {m.operatorProfile && (
                            <div><span style={{ color: palette.g400 }}>Chair:</span> {m.operatorProfile.chairNumber ?? '—'}</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
